import { useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { useProductPartsCount } from 'hooks/orderForms/useProductPartsCount';
import { useProductPricePerUnit } from 'hooks/orderForms/useProductPricePerUnit';
import { registerProductPrice } from 'stores/orderForm/orderForm';
import { formatCurrency } from 'support/helpers/currency/currency';
import { OrderFormProductDTO } from 'support/types/dtos';

import { useProductAttributes } from './hooks/useProductAttributes';
import { useProductIdentifiers } from './hooks/useProductIdentifiers';
import { ProductPartsSection } from './components/ProductPartsSection';
import { ProductUnitMeasure } from './components/ProductUnitMeasure';
import { QuantityInput } from './components/QuantityInput';

type OrderFormProductProps = {
  product: OrderFormProductDTO;
};

export const OrderFormProduct = ({ product }: OrderFormProductProps) => {
  const identifiers = useProductIdentifiers(product);
  const attributes = useProductAttributes(product);
  const pricePerUnit = useProductPricePerUnit(product);
  const productPartsCount = useProductPartsCount(product);
  const hasParts = productPartsCount != null && pricePerUnit != null;

  const displayPrice = product.discounted_unit_price;
  const hasDisplayPrice = displayPrice != null;
  const currency = product.currency;

  useEffect(() => {
    // Inform the store if this product has a price defined
    registerProductPrice(displayPrice);
  }, [displayPrice]);

  const allDisplayItems = useMemo(() => [...identifiers, ...attributes], [identifiers, attributes]);

  const { control } = useFormContext();
  return (
    <div className="flex items-center justify-between gap-4 border-b border-gray-200 py-6">
      <div className="space-y-3">
        <div>
          <h4 className="font-bold text-gray-900">{product.name}</h4>
          {allDisplayItems.length > 0 && (
            <div className="flex gap-2 text-xs text-gray-500">
              {allDisplayItems.map((item, index) => (
                <span
                  key={index}
                  className={`${index < allDisplayItems.length - 1 ? 'border-r border-gray-200 pr-2' : ''}`}
                >
                  {item}
                </span>
              ))}
            </div>
          )}
        </div>
        {product.description && <p className="text-sm text-gray-700">{product.description}</p>}
      </div>
      <div className="flex items-center gap-6">
        <div className="text-right">
          {hasDisplayPrice ? (
            <span className="font-bold text-gray-900" data-testid="price-section">
              {formatCurrency(displayPrice, currency)}
            </span>
          ) : null}
          {hasParts ? <ProductPartsSection product={product} /> : <ProductUnitMeasure product={product} />}
        </div>
        <Controller
          control={control}
          name={`products.${product.id}.quantity`}
          render={({ field: { onChange, value } }) => (
            <QuantityInput productName={product.name} value={value} onChange={onChange} />
          )}
        />
      </div>
    </div>
  );
};
